import { createTheme } from '@mui/material/styles';

// MUI theme mapped onto the mijak design system's semantic tokens
// (see src/ds/tokens/*.css and .claude/skills/mijak-design). Light mode only —
// dark mode is deferred to a follow-up ticket.
//
// Raw hex values below are copied verbatim from src/ds/tokens/colors.css /
// semantic.css (light theme block) so the two stay in lockstep.

const sand = {
  50: '#FAF6EE',
  100: '#F4EEE4',
  200: '#EAE0CF',
  300: '#DCCFB8',
  400: '#C6B79B',
  500: '#A89A80',
  600: '#857966',
  700: '#5C5347',
  800: '#423A30',
  900: '#33291F',
  950: '#241C13',
};

const clay = {
  50: '#FAEDE8',
  100: '#F3D6CC',
  500: '#B6533F',
  600: '#9C4533',
  700: '#7E3829',
};

const sage = {
  50: '#EFF1E8',
  100: '#DCE2CD',
  500: '#6E7E55',
  600: '#5A6845',
};

const amber = {
  50: '#FCF2E1',
  400: '#D98C4A',
  500: '#C2752F',
};

const ember = {
  50: '#FBEAE5',
  500: '#C2402C',
  600: '#A33324',
};

const denim = {
  50: '#EAF0F2',
  500: '#456C7E',
  600: '#395A69',
};

const cardWhite = '#FDFBF6';

// Warm, espresso-tinted shadows (never pure black) — from ds/tokens/elevation.css
const shadow = {
  xs: '0 1px 2px rgba(53, 41, 31, 0.06)',
  sm: '0 1px 2px rgba(53, 41, 31, 0.06), 0 2px 6px -2px rgba(53, 41, 31, 0.10)',
  md: '0 2px 4px rgba(53, 41, 31, 0.06), 0 8px 20px -6px rgba(53, 41, 31, 0.16)',
  lg: '0 4px 8px rgba(53, 41, 31, 0.07), 0 18px 40px -12px rgba(53, 41, 31, 0.22)',
  xl: '0 8px 16px rgba(53, 41, 31, 0.08), 0 32px 64px -16px rgba(53, 41, 31, 0.28)',
};

const borderSubtle = 'rgba(53, 41, 31, 0.08)';

// MUI expects exactly 25 shadow entries (elevations 0-24). We only have
// distinct warm shadows for the few elevations the app actually uses;
// everything else falls back to the nearest defined tier so no pure-black
// default MUI shadow survives.
const shadowByElevation = {
  0: 'none',
  1: shadow.xs,
  2: shadow.sm,
  3: shadow.sm,
  4: shadow.md,
  6: shadow.md,
  8: shadow.lg,
  24: shadow.xl,
};
function fillShadows() {
  const shadows = ['none'];
  let last = shadow.xs;
  for (let i = 1; i <= 24; i++) {
    if (shadowByElevation[i]) last = shadowByElevation[i];
    shadows.push(last);
  }
  return shadows;
}

const theme = createTheme({
  // Kept for SheetView's support panel, which reads theme.supportPanel.background.
  supportPanel: {
    background: sand[200],
  },
  palette: {
    mode: 'light',
    primary: {
      main: clay[500],
      light: clay[100],
      dark: clay[700],
      contrastText: cardWhite,
    },
    secondary: {
      main: sage[500],
      light: sage[100],
      dark: sage[600],
      contrastText: cardWhite,
    },
    error: {
      main: ember[500],
      light: ember[50],
      dark: ember[600],
      contrastText: cardWhite,
    },
    warning: {
      main: amber[500],
      light: amber[50],
      dark: amber[400],
      contrastText: sand[950],
    },
    info: {
      main: denim[500],
      light: denim[50],
      dark: denim[600],
      contrastText: cardWhite,
    },
    success: {
      main: sage[600],
      light: sage[50],
      contrastText: cardWhite,
    },
    background: {
      default: sand[100],
      paper: cardWhite,
    },
    text: {
      primary: sand[800],
      secondary: sand[600],
      disabled: sand[500],
    },
    divider: borderSubtle,
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: "'Figtree', system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontSize: 15,
    h1: { fontFamily: "'Bricolage Grotesque', 'Figtree', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontFamily: "'Bricolage Grotesque', 'Figtree', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' },
    h3: { fontFamily: "'Bricolage Grotesque', 'Figtree', sans-serif", fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontFamily: "'Bricolage Grotesque', 'Figtree', sans-serif", fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontFamily: "'Bricolage Grotesque', 'Figtree', sans-serif", fontWeight: 700, letterSpacing: '-0.015em' },
    h6: { fontFamily: "'Bricolage Grotesque', 'Figtree', sans-serif", fontWeight: 700, letterSpacing: '-0.015em' },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shadows: fillShadows(),
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: sand[100],
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 14,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${borderSubtle}`,
          boxShadow: shadow.sm,
          backgroundColor: cardWhite,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: shadow.xl,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease',
          '&:active': {
            transform: 'translateY(1px)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: sand[900],
          color: sand[50],
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: sand[900],
          color: sand[50],
          borderRadius: 10,
          boxShadow: shadow.lg,
        },
      },
    },
  },
});

export default theme;
